import { pipe } from "effect";
import {
  getProtocol,
  ProdProtocolKey,
  protocolMap,
  ProtocolKey,
} from "./protocol";
import { Schema } from "@effect/schema";
import { satisfies } from "effect/Function";
import { mainnet as ethereum, arbitrum, base, optimism } from "viem/chains";
import type { FallbackTransport, PublicClient as ViemPublicClient } from "viem";
import { anvil, local } from "./viem";
import { Address, makeAddressSync } from "./address";
import { camelToKebab } from "./string";

const chainsArray = Object.values(protocolMap).map(
  (protocol) => protocol.chain
);

export const protocolChainIds = chainsArray.map((chain) => chain.id);

export type Chain = (typeof chainsArray)[number];

export const ChainId = Schema.Literal(
  ethereum.id,
  arbitrum.id,
  optimism.id,
  base.id,
  local.id,
  anvil.id
).annotations({ identifier: "ChainId" });

export type ChainId = typeof ChainId.Type;
export const chainIds = ChainId.literals;
export const isChainId = Schema.is(ChainId);

export const CHAIN_GUARD_1: ChainId[] = protocolChainIds;
export const CHAIN_GUARD_2: typeof protocolChainIds = [...chainIds];

export const ChainKey = Schema.Literal(
  "ethereum",
  "arbitrum",
  "optimism",
  "base",
  "local",
  "anvil"
).annotations({ identifier: "ChainKey" });

export type ChainKey = typeof ChainKey.Type;
export const chainKeys = ChainKey.literals;
export const isChainKey = Schema.is(ChainKey);

export const chainKeyMap = satisfies<Record<ChainId, ChainKey>>()({
  [ethereum.id]: "ethereum",
  [arbitrum.id]: "arbitrum",
  [optimism.id]: "optimism",
  [base.id]: "base",
  [local.id]: "local",
  [anvil.id]: "anvil",
} as const);

export const chains = satisfies<Record<ChainKey, Chain>>()({
  ethereum,
  arbitrum,
  optimism,
  base,
  local,
  anvil,
} as const);

export function getChainKey(id: ChainId): ChainKey {
  return chainKeyMap[id];
}

export function getChain(key: ChainKey): Chain {
  return chains[key];
}

export type PublicClient = ViemPublicClient<FallbackTransport, Chain>;

export function getChainKeyFromProtocol(protocolKey: ProtocolKey): ChainKey {
  return pipe(
    protocolKey,
    getProtocol,
    (protocol) => protocol.chain.id,
    getChainKey
  );
}

const ETHEREUM_WETH = makeAddressSync(
  "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2"
);

const wrappedNativeTokenAddresses: Record<ChainKey, Address[]> = {
  ethereum: [ETHEREUM_WETH],
  arbitrum: [makeAddressSync("0x82aF49447D8a07e3bd95BD0d56f35241523fBab1")],
  optimism: [makeAddressSync("0x4200000000000000000000000000000000000006")],
  base: [makeAddressSync("0x4200000000000000000000000000000000000006")],
  local: [ETHEREUM_WETH],
  anvil: [ETHEREUM_WETH],
};

export function getIsWrappedNative(chainKey: ChainKey) {
  return (tokenAddress: Address) =>
    wrappedNativeTokenAddresses[chainKey].includes(tokenAddress);
}

export function getChainSlug(chainKey: ChainKey): string {
  return camelToKebab(chainKey);
}

export type ProdChainKey = {
  [K in ProdProtocolKey]: (typeof chainKeyMap)[(typeof protocolMap)[K]["chain"]["id"]];
}[ProdProtocolKey];

/**
 * Check on `prodChainKeys`
 */
export const guard: Record<ProdChainKey, true> = {
  arbitrum: true,
  optimism: true,
  base: true,
  ethereum: true,
};

export const prodChainKeys = [
  // export const prodChainKeys: UnionToTuple<ProdChainKey> = [
  "arbitrum",
  "optimism",
  "base",
  "ethereum",
] as const;

export const ProdChainKey = Schema.Literal(...prodChainKeys).annotations({
  identifier: "ProdChainKey",
});
export const isProdChainKey = Schema.is(ProdChainKey);

export function getProdChainKeyFromProtocol(
  prodProtocolKey: ProdProtocolKey
): ProdChainKey {
  return pipe(
    prodProtocolKey,
    (key) => protocolMap[key],
    (protocol) => protocol.chain.id,
    (chainId) => chainKeyMap[chainId]
  );
}
